import React, { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { m } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import NotFoundPage from './NotFoundPage';
import { getGuideBySlug } from './guideArticlesData';
import { internalUrl, SITE_ORIGIN } from './siteConfig';

function GuideArticlePage() {
  const { slug } = useParams();
  const guide = slug ? getGuideBySlug(slug) : null;

  const canonical = guide ? `${SITE_ORIGIN}/guides/${guide.slug}` : `${SITE_ORIGIN}/guides`;
  const schemaId = guide ? `webcodecraft-guide-${guide.slug}` : '';

  const jsonLd = useMemo(() => {
    if (!guide) return null;
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: guide.title,
      description: guide.description,
      datePublished: guide.datePublished,
      dateModified: guide.datePublished,
      author: { '@type': 'Organization', name: 'WebCodeCraft', url: SITE_ORIGIN },
      publisher: { '@type': 'Organization', name: 'WebCodeCraft', url: SITE_ORIGIN },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonical }
    });
  }, [guide, canonical]);

  useEffect(() => {
    if (!guide) return undefined;
    document.title = `${guide.title} | WebCodeCraft`;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content');
    meta?.setAttribute('content', guide.description);
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanon = canonical?.getAttribute('href');
    canonical?.setAttribute('href', `${SITE_ORIGIN}/guides/${guide.slug}`);

    const existing = document.getElementById(schemaId);
    if (!existing && jsonLd) {
      const el = document.createElement('script');
      el.id = schemaId;
      el.type = 'application/ld+json';
      el.textContent = jsonLd;
      document.head.appendChild(el);
    }

    return () => {
      if (prevDesc != null) meta?.setAttribute('content', prevDesc);
      if (prevCanon != null) canonical?.setAttribute('href', prevCanon);
      const script = document.getElementById(schemaId);
      if (script) script.remove();
      document.title = 'WebCodeCraft | Devon web design — no upfront cost';
    };
  }, [guide, jsonLd, schemaId]);

  if (!guide) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Header />
      <main
        id="main-content"
        className="pt-24 pb-16 min-w-0 overflow-x-clip bg-gradient-to-b from-gray-50 to-white min-h-screen"
      >
        <article className="container mx-auto px-4 max-w-3xl">
          <m.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="text-sm text-indigo-600 font-medium mb-4">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <Link to="/guides" className="hover:underline">
                Guides
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <span className="text-gray-600 line-clamp-2">{guide.title}</span>
            </p>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">{guide.title}</h1>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed">{guide.description}</p>

            <div className="space-y-10 text-gray-700 leading-relaxed">
              {guide.sections.map((sec, idx) => (
                <section key={idx} aria-labelledby={`guide-sec-${guide.slug}-${idx}`}>
                  <h2 id={`guide-sec-${guide.slug}-${idx}`} className="text-2xl font-bold text-gray-900 mb-4">
                    {sec.h2}
                  </h2>
                  {sec.paragraphs.map((p, j) => (
                    <p key={j} className="mb-4 last:mb-0">
                      {p}
                    </p>
                  ))}
                </section>
              ))}
            </div>

            <div className="mt-12 p-6 rounded-2xl bg-slate-50 border border-slate-200 text-slate-700 text-sm">
              <p className="font-medium text-slate-900 mb-2">Related on WebCodeCraft</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <Link to="/check-your-seo" className="text-indigo-600 font-medium hover:underline">
                    Free SEO audit
                  </Link>
                </li>
                <li>
                  <Link to="/accessibility-check" className="text-indigo-600 font-medium hover:underline">
                    Accessibility scan
                  </Link>
                </li>
                <li>
                  <Link to="/services/local-seo" className="text-indigo-600 font-medium hover:underline">
                    Local SEO service pillar
                  </Link>
                </li>
                <li>
                  <Link to="/guides" className="text-indigo-600 font-medium hover:underline">
                    All guides
                  </Link>
                </li>
              </ul>
            </div>

            <div className="mt-10 p-6 rounded-2xl bg-gradient-to-r from-indigo-600 to-cyan-600 text-white">
              <h3 className="text-xl font-bold mb-2">Questions about your site?</h3>
              <p className="mb-4 opacity-95">
                Tell us your goals — we reply with honest fit and next steps.
              </p>
              <a
                href={internalUrl('contact')}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
              >
                Contact WebCodeCraft
              </a>
            </div>
          </m.div>
        </article>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default GuideArticlePage;
